<template>
  <div class="address-row address-row--new address-list__row">
    <div class="address-row-alias">
      <div class="input-text">
        <input
          v-model="alias"
          :class="{ error: errors.alias }"
          :placeholder="trans('addresses.body.table.form.name.placeholder')"
          :required="true"
          class="address-row-alias__input"
          type="text"
        ><label v-if="errors.alias" class="error">{{ errors.alias }}</label>
      </div>
    </div>

    <div :class="{ error: errors.address }" class="address-row__address address-row__address--edit">
      <div class="input-text">
        <input
          :id="`update-address-${address.id}`"
          v-model="name"
          :class="{ error: errors.address }"
          :placeholder="trans('addresses.body.table.form.address.placeholder')"
          type="text"
        >
        <span v-if="googleAddress.name" class="address-row__address--empty" @click="emptyGoogleAddress" />
        <label v-if="errors.address" class="error">{{ errors.address }}</label>
      </div>
    </div>

    <div class="address-row__actions row-actions">
      <button
        :class="{ disabled: !canValidate, 'loading-btn': saveLoading }"
        class="row-actions__validate btn btn--primary btn--with-icon"
        @click="onValidate"
      >
        <span v-if="!saveLoading">{{ trans('addresses.body.table.actions.validate') }}</span>
      </button>
      <button class="row-actions__cancel btn btn--secondary" @click="onCancel">
        <span>{{ trans('addresses.body.table.actions.cancel') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import eventBus from '@tools/event-bus';
import { flashBagError, flashBagSuccess } from '@tools/flashbag';
import Logger from '@tools/logger/logger';
import requests from '@tools/requests-mixin';
import { URL_XHR_ADDRESS_ID, URL_XHR_ADDRESSES } from '@tools/routes/routes';
import { translations } from '@tools/translations/translations';
import translation from '@elements/translation';
import AutocompleteCustom from '@atoms/inputs/input-location';
import googlePlacesApiUtils, { ERR_STREET_NR, ERR_ZIPCODE } from '@molecules/google-places-api-utils';

export default {
  mixins: [ translation, googlePlacesApiUtils, requests ],
  props: {
    address: {
      type: Object,
      default: () => ({
        id: 0,
        name: '',
        street: '',
        number: '',
        city: '',
        zip_code: '',
      }),
      required: false,
    },
  },
  data() {
    return {
      errors: { address: null, alias: null },
      googleAddress: this.mapEntityToPlace(this.address),
      saveLoading: false,
      addressFilledWithGoogleMapResult: false,
    };
  },
  computed: {
    alias: {
      get() {
        return this.googleAddress.alias;
      },
      set(alias) {
        this.errors.alias = alias.length === 0 ? translations.validators['global.form.field.required'] : null;
        this.googleAddress.alias = alias;
      },
    },
    name: {
      get() {
        return this.googleAddress.name;
      },
      set(name) {
        this.errors.address = name.length === 0 ? translations.validators['global.form.field.required'] : null;
        this.googleAddress.name = name;
      },
    },
    creation() {
      return this.address.id === 0;
    },
    canValidate() {
      return this.alias.length > 0
        && this.name.length > 0
        && this.errors.alias === null
        && this.errors.address === null
        && (this.addressFilledWithGoogleMapResult || !this.creation);
    },
  },
  mounted() {
    this.addressElement = document.getElementById(`update-address-${this.address.id}`);
    const customAutocomplete = new AutocompleteCustom(this.addressElement);
    customAutocomplete.init();

    eventBus.addEventListener('input-location-change', this.handleInputLocationChange);
  },
  methods: {
    handleInputLocationChange(event) {
      const { place, field } = event.detail;
      if (field === this.addressElement) {
        this.fillFormWithGoogleMapResult(place);
      }
    },
    emptyGoogleAddress() {
      this.googleAddress.name = '';
      this.googleAddress.street = '';
      this.googleAddress.zip_code = '';
      this.errors.address = translations.validators['global.form.field.required'];
    },
    async onValidate() {
      if (this.saveLoading) {
        return;
      }

      if (this.googleAddress.name === '') {
        this.errors.address = translations.validators['global.form.field.required'];
        return;
      }

      this.saveLoading = true;
      const body = { name: this.googleAddress?.alias, ...this.mapPlaceToEntity(this.googleAddress, this.address) };
      delete body.id;

      if (this.creation) {
        try {
          await this.post(URL_XHR_ADDRESSES, body);
          flashBagSuccess(this.trans('addresses.body.table.form.message.add_success'));
          this.$emit('created');
        } catch (err) {
          if (err.response.data.address_fields_name) {
            [ this.errors.alias ] = err.response.data.address_fields_name;
          } else {
            [ this.errors.address ] = Object.values(err.response.data);
            flashBagError();
          }
          this.saveLoading = false;
        }
      } else {
        try {
          await this.put(URL_XHR_ADDRESS_ID.replace('{id}', this.address.id), body);
          flashBagSuccess(this.trans('addresses.body.table.form.message.success'));
          this.$emit('edited');
        } catch (err) {
          if (err.response.data.address_fields_name) {
            [ this.errors.alias ] = err.response.data.address_fields_name;
          } else {
            [ this.errors.address ] = Object.values(err.response.data);
            flashBagError();
          }
          this.saveLoading = false;
        }
      }
    },
    onCancel() {
      this.$emit('cancelled');
    },
    fillFormWithGoogleMapResult(place) {
      this.googleAddress.name = place.name;
      Logger.info('Google result:', place);
      switch (this.checkPlace(place)) {
      case ERR_ZIPCODE:
        this.addressFilledWithGoogleMapResult = false;
        this.errors.address = this.trans('addresses.body.table.form.address.error');
        return;
      case ERR_STREET_NR:
        this.addressFilledWithGoogleMapResult = false;
        this.errors.address = this.trans('addresses.body.table.form.address.no_number');
        return;
      default:
        this.addressFilledWithGoogleMapResult = true;
        this.errors.address = null;
      }
      this.googleAddress = { alias: this.alias, ...this.setAddressFromPlace(place) };
    },
  },
};
</script>
